import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { outgoingPostbackApi } from "./api";

import GetOutgoingPostbacksResponse = App.Http.Responses.Tracking.GetOutgoingPostbacksResponse;
import GetOutgoingPostbackResponse = App.Http.Responses.Tracking.GetOutgoingPostbackResponse;
import StoreOutgoingPostbackRequest = App.Http.Requests.Tracking.StoreOutgoingPostbackRequest;
import GetConnectionSequenceStepsExportResponse = App.Http.Responses.ConnectionCreator.SequenceStep.GetConnectionSequenceStepsExportResponse;
import GetOutgoingPostbacksRequest = App.Http.Requests.Tracking.GetOutgoingPostbacksRequest;
// import GetOutgoingPostbackRequest = App.Http.Requests.Tracking.GetOutgoingPostbackRequest;
import StoreOutgoingPostbackConnectionSequenceStepsRequest = App.Http.Requests.Tracking.StoreOutgoingPostbackConnectionSequenceStepsRequest;
import UpdateOutgoingPostbackRequest = App.Http.Requests.Tracking.UpdateOutgoingPostbackRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import TestOutgoingPostbackRequest = App.Http.Requests.Tracking.TestOutgoingPostbackRequest;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;

// Query to get a list of Outgoing postbacks
export const useGetOutgoingPostbacksQuery = (request: GetOutgoingPostbacksRequest, options = {}) => {
    return useQueryApi<GetOutgoingPostbacksResponse>({
        queryKey: ["getOutgoingPostbacks", request],
        queryFn: () => outgoingPostbackApi.index(request),
        ...options,
    });
};

// Query to get a specific Outgoing postback
export const useGetOutgoingPostbackQuery = (postbackId: string, request: unknown, options = {}) => {
    return useQueryApi<GetOutgoingPostbackResponse>({
        queryKey: ["getOutgoingPostback", postbackId, request],
        queryFn: () => outgoingPostbackApi.show(postbackId, request),
        ...options,
    });
};

// Mutation to store a new Outgoing postback
export const useStoreOutgoingPostbackMutation = (
    options: UseMutationOptions<
        GetOutgoingPostbackResponse,
        AxiosError<ApiErrorResponse>,
        StoreOutgoingPostbackRequest
    > = {}
) => {
    return useMutationApi<GetOutgoingPostbackResponse, StoreOutgoingPostbackRequest>({
        mutationKey: ["storeOutgoingPostback"],
        mutationFn: (request: StoreOutgoingPostbackRequest) => outgoingPostbackApi.store(request),
        ...options,
    });
};

// Mutation to update an existing Outgoing postback
export const useUpdateOutgoingPostbackMutation = (id: string, options = {}) => {
    return useMutationApi<GetOutgoingPostbackResponse, UpdateOutgoingPostbackRequest>({
        mutationKey: ["updateOutgoingPostback"],
        mutationFn: (request: UpdateOutgoingPostbackRequest) => outgoingPostbackApi.update(request, id),
        ...options,
    });
};

// Mutation to destroy a Outgoing postback
export const useDestroyOutgoingPostbackMutation = (options = {}) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyOutgoingPostback"],
        mutationFn: (postbackId: string) => outgoingPostbackApi.destroy(postbackId),
        ...options,
    });
};

// Query to get a specific OutgoingPostback in export format
export const useGetOutgoingPostbackExportQuery = (
    outgoingPostbackId: string,
    request?: GetOutgoingPostbacksRequest,
    options = {}
) => {
    return useQueryApi<GetOutgoingPostbackResponse>({
        queryKey: ["getOutgoingPostbackExport", outgoingPostbackId],
        queryFn: () => outgoingPostbackApi.showExport(outgoingPostbackId, request),
        ...options,
    });
};

// Query to get a specific OutgoingPostback connection sequence steps in export format
export const useGetOutgoingPostbackMethodsExportQuery = (
    outgoingPostbackId: string,
    request?: GetOutgoingPostbacksRequest,
    options = {}
) => {
    return useQueryApi<GetConnectionSequenceStepsExportResponse>({
        queryKey: ["getOutgoingPostbackMethodsExport", outgoingPostbackId],
        queryFn: () => outgoingPostbackApi.showMethodsExport(outgoingPostbackId, request),
        ...options,
    });
};

// Mutation to update an existing OutgoingPostback methods using PUT
export const useUpdateOutgoingPostbackMethodsMutation = (outgoingPostbackId: string, options = {}) => {
    return useMutationApi<
        GetConnectionSequenceStepsExportResponse,
        StoreOutgoingPostbackConnectionSequenceStepsRequest
    >({
        mutationKey: ["updateOutgoingPostbackMethods", outgoingPostbackId],
        mutationFn: (request: StoreOutgoingPostbackConnectionSequenceStepsRequest) =>
            outgoingPostbackApi.updateMethods(request, outgoingPostbackId),
        ...options,
    });
};

export const useExportOutgoingPostbacksMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportOutgoingPostbacks"],
        mutationFn: (request) => outgoingPostbackApi.export(request),
        ...options,
    });
};

// Mutation to test outgoing postback
export const useTestOutgoingPostbackMutation = (
    options: UseMutationOptions<
        TestConnectionSequenceStepResponse,
        AxiosError<ApiErrorResponse>,
        { outgoingPostbackId: string; request: TestOutgoingPostbackRequest }
    > = {}
) => {
    return useMutationApi<
        TestConnectionSequenceStepResponse,
        { outgoingPostbackId: string; request: TestOutgoingPostbackRequest }
    >({
        mutationKey: ["testOutgoingPostback"],
        mutationFn: ({ outgoingPostbackId, request }) => outgoingPostbackApi.test(outgoingPostbackId, request),
        ...options,
    });
};
